/**
 * Eric 2026-06-25 (iter121 — Session A4) — Sous-onglet Fireflies de la
 * transcription d'une vidéo.
 *
 * Extrait de VideoDetailPage.js (refacto P0, cible <1500 lignes).
 *
 * Affiché uniquement quand `transcriptSubTab === "fireflies"`. Contient :
 *   - Barre d'actions Fireflies (Historique + URLs + Regrouper + Régénérer
 *     la fusion). Gated par `canEdit`. Vincent #355 — Lecture seule sinon.
 *   - Bouton "Remplacer" (admin only) qui efface la transcription Fireflies
 *     courante pour permettre un nouvel import.
 *   - Liste des segments Fireflies en format 3-cols (timecode | locuteur |
 *     texte), avec auto-scroll sur le segment actif (parité Whisper UX).
 *   - Fallback "aucun segment parsable" : phrases extraites du full_text.
 *
 * Props :
 *   - show, canEdit, isAdmin — gating.
 *   - lang, api, videoId — config.
 *   - allTranscripts, setAllTranscripts, transcriptsLoading — données.
 *   - États partagés : urlValidating, crossValidating, etc.
 *   - Handlers : setShowHistory, loadRevisions, loadVideoData,
 *     loadAllTranscripts, pollMergedTranscript, seekTo.
 *   - Helpers : speakerMap, currentTime, activeSegmentRef,
 *     parseTextTranscriptToSegments, formatTimeBracket.
 */
import React, { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { confirmDialog } from "../../lib/uiDialogs";
import {
  ClockCounterClockwise,
  ArrowClockwise,
  SpinnerGap,
  Broom,
} from "@phosphor-icons/react";
import { formatSpeakerLines } from "../../lib/speakerFormat";
import { GROUP_RERUN, GROUP_UTIL, ToolbarSeparator, ToolbarGroupLabel } from "../../lib/transcriptToolbarStyles";
import { useCollapsedToolbarGroups } from "../../lib/useCollapsedToolbarGroups";
import WebCorrectionBadge from "./WebCorrectionBadge";

export const FirefliesSubTab = ({
  show,
  // Vincent B954 (Eric 2026-02-14 SPEC STRICK PROD) — Cf WhisperSubTab.
  render,
  canEdit,
  isAdmin,
  lang,
  api,
  videoId,
  allTranscripts,
  setAllTranscripts,
  transcriptsLoading,
  // États partagés
  urlValidating,
  setUrlValidating,
  setUrlProposals,
  // Handlers parent
  setShowHistory,
  loadRevisions,
  loadVideoData,
  loadAllTranscripts,
  // Helpers transcript
  speakerMap,
  currentTime,
  activeSegmentRef,
  seekTo,
  parseTextTranscriptToSegments,
  formatTimeBracket,
  // Vincent B945v2 (Eric 2026-02-14 SPEC STRICK PROD) — Injecte les boutons
  // fusion (Reglages, Refaire) juste avant « URLs » dans la toolbar.
  extraFusionButtons = null,
  // Vincent B970 (Eric 2026-02-14 SPEC STRICK PROD) — Bouton « Remplacer
  // transcription Fireflies » injecte en PREMIERE position de la toolbar
  // (single-block). Auparavant dans Line 2 de TranscriptPanel.
  replaceFirefliesBtn = null,
}) => {
  // Vincent B754 (Eric 2026-02-17) — Hook `reparsing` déclaré AVANT tout
  // early-return (règle des hooks).
  const [reparsing, setReparsing] = useState(false);
  // Tâche 1312 — Blocs repliables, choix mémorisé.
  const { isCollapsed, toggleGroup } = useCollapsedToolbarGroups();

  if (!show) return null;

  // Vincent B970 (Eric 2026-02-14 SPEC STRICK PROD) — On ne retourne PLUS
  // `null` quand aucune transcription Fireflies n'existe. On veut que la
  // toolbar (avec Remplacer, Reparser, Purger, Réglages, Refaire fusion,
  // URLs, Historique) soit TOUJOURS visible pour permettre l'import et
  // les actions preparatoires. Les handlers referencent `allTranscripts?
  // .fireflies` en toute sécurité (optional chaining).
  //
  // Vincent B970 rev2 (Eric 2026-02-26 SPEC STRICK PROD) — Le loading
  // spinner NE DOIT PAS masquer la toolbar quand `render === "toolbar"`
  // (sinon plus rien à l'écran pendant le chargement). On garde le
  // spinner uniquement pour le mode segments (undefined + "segments").
  if (!allTranscripts?.fireflies && transcriptsLoading && render !== "toolbar") {
    return (
      <div className="p-10 text-center text-zinc-400" data-testid="fireflies-loading">
        <SpinnerGap size={28} className="mx-auto mb-3 animate-spin opacity-50" />
        <p className="text-sm">{lang === "fr" ? "Chargement..." : "Loading..."}</p>
      </div>
    );
  }
  const _hasFireflies = !!allTranscripts?.fireflies;

  const handleValidateUrls = async () => {
    if (urlValidating) return;
    setUrlValidating(true);
    setUrlProposals(null);
    try {
      const { data } = await axios.post(
        `${api}/api/videos/${videoId}/validate-urls`,
        {},
        { withCredentials: true }
      );
      setUrlProposals(data);
      if ((data.proposals || []).length > 0) {
        toast.info(
          lang === "fr"
            ? `${data.detected} URL(s) détectée(s) — voir l'onglet Fusionnée`
            : `${data.detected} URL(s) detected — see Merged tab`
        );
      }
    } catch (err) {
      toast.error(
        lang === "fr"
          ? `Erreur : ${err.response?.data?.detail || err.message}`
          : `Error: ${err.response?.data?.detail || err.message}`
      );
    }
    setUrlValidating(false);
  };

  const handleRegroup = async () => {
    const ok = await confirmDialog(
      lang === "fr"
        ? "Regrouper les paragraphes en blocs d'environ 40 s (max 1 min 30) par locuteur ? Cette action s'applique aux segments Whisper puis re-fusionne avec Fireflies."
        : "Regroup paragraphs into ~30s per-speaker blocks (cap 1min30)? Applies to Whisper segments then re-merges with Fireflies.",
      { confirmLabel: lang === "fr" ? "Regrouper" : "Regroup", tone: "warning" },
    );
    if (!ok) return;
    try {
      const { data } = await axios.post(
        `${api}/api/videos/${videoId}/regroup-segments`,
        {},
        { withCredentials: true }
      );
      toast.success(
        lang === "fr"
          ? `Regroupement OK : ${data.raw_segments} → ${data.grouped_segments} paragraphes`
          : `Regroup OK: ${data.raw_segments} → ${data.grouped_segments} paragraphs`
      );
      await loadVideoData();
      await loadAllTranscripts();
    } catch (err) {
      toast.error(
        lang === "fr"
          ? `Erreur : ${err.response?.data?.detail || err.message}`
          : `Error: ${err.response?.data?.detail || err.message}`
      );
    }
  };

  // Vincent B754 (Eric 2026-02-17) — Bouton « Réanalyser Fireflies ».
  // Rejoue le parser Fireflies (améliorations : traits horizontaux,
  // séparateurs em-dash) sur un DOCX déjà présent. Utile pour corriger
  // rétroactivement d'anciens uploads.
  const handleReparseFireflies = async () => {
    const ok = await confirmDialog(
      lang === "fr"
        ? "Réanalyser la transcription Fireflies avec le parser à jour ?\n\nUtile si le formatage actuel comporte des traits horizontaux, des séparateurs — ou —, ou des horodatages mal détectés. Les segments seront remplacés (le texte source est conservé). Vous devrez ensuite « Refaire la fusion » pour propager la correction."
        : "Re-run the Fireflies parser with the latest version?\n\nUseful if the current formatting has horizontal rules, em-dash separators, or missing timestamps.",
      { confirmLabel: lang === "fr" ? "Réanalyser" : "Reparse", tone: "warning" },
    );
    if (!ok) return;
    setReparsing(true);
    try {
      const { data } = await axios.post(
        `${api}/api/videos/${videoId}/reparse-fireflies`,
        {},
        { withCredentials: true },
      );
      toast.success(
        lang === "fr"
          ? `Réanalyse OK : ${data.segments_count} segments extraits (${data.chars.toLocaleString()} caractères).`
          : `Reparse OK: ${data.segments_count} segments (${data.chars.toLocaleString()} chars).`
      );
      await loadAllTranscripts?.();
    } catch (err) {
      toast.error(
        lang === "fr"
          ? `Erreur : ${err.response?.data?.detail || err.message}`
          : `Error: ${err.response?.data?.detail || err.message}`
      );
    } finally {
      setReparsing(false);
    }
  };

  const handleReplace = async () => {
    const ok = await confirmDialog(
      lang === "fr"
        ? "Remplacer la transcription Fireflies actuelle ? Cette action supprime uniquement la version Fireflies + la version fusionnée. La transcription Whisper et la reconnaissance des locuteurs sont conservées (aucun crédit AI recalculé). Tu pourras ensuite importer un nouveau fichier Fireflies puis relancer la fusion."
        : "Replace current Fireflies transcript? This only removes the Fireflies version and the merged version. Whisper transcript and speaker diarization are preserved (no AI credits re-spent). You can then import a new Fireflies file and re-run the fusion.",
      { danger: true, confirmLabel: lang === "fr" ? "Remplacer" : "Replace" },
    );
    if (!ok) return;
    try {
      await axios.delete(`${api}/api/videos/${videoId}/fireflies-transcript`, {
        withCredentials: true,
      });
      setAllTranscripts((prev) =>
        prev
          ? {
              ...prev,
              fireflies: null,
              merged: null,
              summary: { ...prev.summary, has_fireflies: false, has_merged: false },
            }
          : prev
      );
    } catch (e) {
      console.error(e);
      toast.error(
        lang === "fr"
          ? `Erreur : ${e.response?.data?.detail || e.message}`
          : `Error: ${e.response?.data?.detail || e.message}`
      );
    }
  };

  // Calcul de la liste de segments à afficher : segments structurés en
  // priorité, sinon parsing du full_text.
  const _ffDoc = allTranscripts?.fireflies || {};
  const realSegs = Array.isArray(_ffDoc.segments) && _ffDoc.segments.length > 0
    ? _ffDoc.segments
    : (_ffDoc.full_text ? parseTextTranscriptToSegments(_ffDoc.full_text) : []);

  const rawText = (_ffDoc.full_text || "").trim();
  const sentences = realSegs.length === 0 ? rawText.split(/(?<=[.!?])\s+/).filter(Boolean) : [];

  return (
    <>
      {/* Vincent B970 rev2 (Eric 2026-02-26 SPEC STRICK PROD) — La
          toolbar est rendue AU TOP LEVEL (hors du container min-h)
          pour deux raisons :
            1. Bug : quand render === "toolbar", l'ancien wrapper
               `render === undefined || render === "segments"` skippait
               tout le contenu et le toolbar disparaissait.
            2. Le container min-h-[300px] ajoutait un grand espace vide
               sous le toolbar en mode toolbar-only. */}
      {canEdit && (render === undefined || render === "toolbar") && (
        <div
          className="px-3 py-2.5 bg-amber-50 border-b border-amber-200 text-[11px]"
          data-testid="fireflies-actions-bar"
        >
          <div className="flex items-center gap-2 flex-wrap">
            {/* Tâche 1309 (Eric 2026-06-30) — Le mot « Fireflies : » porte ce
                que les boutons ne répètent plus (« Remplacer », « Reparser »). */}
            <ToolbarGroupLabel
              testid="toolbar-group-label-fireflies"
              collapsed={isCollapsed("fireflies")}
              onToggle={() => toggleGroup("fireflies")}
              count={3}
            >
              {lang === "fr" ? "Fireflies :" : "Fireflies:"}
            </ToolbarGroupLabel>
            {!isCollapsed("fireflies") && (
              <>
            {/* Vincent B970 (Eric 2026-02-14 SPEC STRICK PROD) — 1. Remplacer
                (toujours visible, GROUP_UTIL). */}
            {replaceFirefliesBtn}
            {/* 2. Reparser (GROUP_RERUN) */}
            <button
              onClick={handleReparseFireflies}
              disabled={reparsing || !allTranscripts?.summary?.has_fireflies}
              className={`${GROUP_RERUN} disabled:opacity-50 disabled:cursor-not-allowed`}
              data-testid="reparse-fireflies-btn"
              data-button-group="rerun"
              title={
                !allTranscripts?.summary?.has_fireflies
                  ? (lang === "fr"
                      ? "Importe d'abord une transcription Fireflies pour activer ce bouton."
                      : "Import a Fireflies transcript first to enable this button.")
                  : (lang === "fr"
                      ? "Rejoue le parser Fireflies avec les dernières améliorations (traits horizontaux ignorés, séparateurs em-dash retirés, horodatages tolérants)."
                      : "Re-run the Fireflies parser with latest improvements.")
              }
            >
              <ArrowClockwise size={12} weight="bold" />
              {reparsing
                ? (lang === "fr" ? "Réanalyse…" : "Reparsing…")
                : (lang === "fr" ? "Reparser" : "Reparse")}
            </button>
            {/* 3. Purger segments vides Fireflies (GROUP_UTIL) */}
            <button
              onClick={async () => {
                const ok = await confirmDialog(
                  lang === "fr"
                    ? "Purger les segments Fireflies vides (parser B911) ? Traite toutes les vidéos de l'org — idempotent."
                    : "Purge empty Fireflies segments (B911 parser)? Runs on all org videos — idempotent.",
                  { tone: "warning" }
                );
                if (!ok) return;
                const tid = toast.loading(lang === "fr" ? "🧹 Purge en cours…" : "🧹 Purging…", { duration: Infinity });
                try {
                  const { data } = await axios.post(
                    `${process.env.REACT_APP_BACKEND_URL}/api/admin/fireflies/purge-empty-segments`,
                    {},
                    { withCredentials: true, timeout: 60000 }
                  );
                  toast.success(
                    lang === "fr"
                      ? `🧹 Purge OK : ${data.total_purged || 0} segment(s) vide(s) retiré(s) sur ${data.total_scanned || 0} vidéo(s).`
                      : `🧹 Done: ${data.total_purged || 0} empty segment(s) removed on ${data.total_scanned || 0} video(s).`,
                    { id: tid, duration: 10000 }
                  );
                } catch (e) {
                  toast.error(
                    (lang === "fr" ? "Échec : " : "Failed: ") + (e.response?.data?.detail || e.message),
                    { id: tid, duration: 10000 }
                  );
                }
              }}
              disabled={!allTranscripts?.summary?.has_fireflies}
              className={`${GROUP_UTIL} disabled:opacity-50 disabled:cursor-not-allowed`}
              data-testid="purge-empty-fireflies-btn"
              data-button-group="util"
              title={!allTranscripts?.summary?.has_fireflies
                ? (lang === "fr"
                    ? "Importe d'abord une transcription Fireflies pour activer ce bouton."
                    : "Import a Fireflies transcript first to enable this button.")
                : (lang === "fr"
                    ? "Purge rétroactive des segments Fireflies vides pour toute l'org (idempotent)"
                    : "Retroactive purge of empty Fireflies segments across the org (idempotent)")}
            >
              <Broom size={12} weight="bold" />
              {lang === "fr" ? "Purger segments vides" : "Purge empty segments"}
            </button>
              </>
            )}
            {/* Vincent B970 rev (Eric 2026-02-26 SPEC STRICK PROD) —
                Séparateur entre le bloc « Fireflies-only » (Remplacer,
                Reparser, Purger) et le bloc « Fusion » (Réglages fusion,
                Refaire la fusion). */}
            <ToolbarSeparator />
            {/* Vincent B945v2 (Eric 2026-02-14 SPEC STRICK PROD) —
                Boutons fusion (Reglages, Refaire) injectes depuis
                TranscriptPanel, juste avant « URLs ». */}
            {extraFusionButtons}
            {/* Vincent B970 rev (Eric 2026-02-26 SPEC STRICK PROD) —
                Séparateur entre le bloc « Fusion » et le bloc final
                « Utilitaires » (URLs, Historique). */}
            <ToolbarSeparator />
            {/* 4. URLs (GROUP_UTIL) — B970 : disabled si !has_fireflies */}
            <button
              onClick={handleValidateUrls}
              disabled={urlValidating || !allTranscripts?.summary?.has_fireflies}
              className={`${GROUP_UTIL} disabled:opacity-50 disabled:cursor-not-allowed`}
              data-testid="validate-urls-fireflies-btn"
              data-button-group="util"
              title={!allTranscripts?.summary?.has_fireflies
                ? (lang === "fr"
                    ? "Importe d'abord une transcription Fireflies pour activer ce bouton."
                    : "Import a Fireflies transcript first to enable this button.")
                : (lang === "fr"
                    ? "Detecter les URLs prononcees et vérifier qu'elles existent"
                    : "Detect spoken URLs and verify they exist")}
            >
              🔗 {urlValidating ? "..." : "URLs"}
            </button>
            {/* 5. Historique (GROUP_UTIL) — B970 : disabled si !has_fireflies */}
            <button
              onClick={() => {
                setShowHistory(true);
                loadRevisions();
              }}
              disabled={!allTranscripts?.summary?.has_fireflies}
              className={`${GROUP_UTIL} disabled:opacity-50 disabled:cursor-not-allowed`}
              data-testid="fireflies-transcript-history-btn"
              data-button-group="util"
              title={!allTranscripts?.summary?.has_fireflies
                ? (lang === "fr"
                    ? "Importe d'abord une transcription Fireflies pour activer ce bouton."
                    : "Import a Fireflies transcript first to enable this button.")
                : (lang === "fr" ? "Historique des modifications" : "Modification history")}
            >
              <ClockCounterClockwise size={12} weight="bold" />
              {lang === "fr" ? "Historique" : "History"}
            </button>
          </div>
        </div>
      )}
      {/* Vincent B954 — Bannière + segments sont dans un container
          scrollable AVEC min-h-[300px] pour préserver l'espace de
          rendu. Ce bloc n'est rendu QUE en mode segments (undefined
          ou "segments"). */}
      {(render === undefined || render === "segments") && (
      <div
        className="max-h-[calc(100vh-320px)] min-h-[300px] overflow-y-auto"
        data-testid="fireflies-container"
      >
      {/* Vincent B913 (Eric 2026-02-14 SPEC STRICK PROD) — Bannière
          informative quand le parser Fireflies a silencieusement filtré
          des segments sans texte à l'import. Le rapport est persisté
          sur la vidéo (`fireflies_import_report`). Affichée uniquement
          si empty_segments_skipped > 0 pour ne pas polluer les imports
          propres. Éphémère mais utile — Vincent voit quand le parser
          filtre agressivement. */}
      {(() => {
        const rpt = allTranscripts?.video?.fireflies_import_report
          || allTranscripts?.fireflies_import_report;
        const n = Number(rpt?.empty_segments_skipped || 0);
        if (n <= 0) return null;
        return (
          <div
            className="px-3 py-2 bg-amber-50 border-b border-amber-200"
            data-testid="fireflies-empty-segments-banner"
            data-empty-count={n}
          >
            <p className="text-[11px] text-amber-900">
              <span className="font-semibold">⚠ {n} segment{n > 1 ? "s" : ""} Fireflies sans texte détecté{n > 1 ? "s" : ""}</span>
              {" — "}
              <span>purgé{n > 1 ? "s" : ""} silencieusement à l&apos;import</span>
              {rpt?.kept_segments != null && (
                <span className="text-amber-700 ml-1">({rpt.kept_segments} segment{rpt.kept_segments > 1 ? "s" : ""} conservé{rpt.kept_segments > 1 ? "s" : ""})</span>
              )}
              {". "}
              <span className="text-amber-700 italic">
                Ces blocs contenaient seulement un locuteur + horodatage sans contenu utile.
              </span>
            </p>
          </div>
        );
      })()}
      <div className="p-4">
        <div className="mb-3 flex items-center justify-between">
          <p className="text-[11px] text-zinc-500">
            {_ffDoc.filename || "Fireflies"}
            {_ffDoc.created_at &&
              ` · ${new Date(_ffDoc.created_at).toLocaleDateString("fr-FR")}`}
          </p>
          {isAdmin && (
            <button
              className="text-[11px] text-[#002FA7] hover:underline"
              onClick={handleReplace}
              data-testid="replace-fireflies-btn"
            >
              {lang === "fr" ? "Remplacer" : "Replace"}
            </button>
          )}
        </div>
        {/* Vincent (2026-02) #530 followup — Bandeau de preview du parsing
            AVANT fusion. Affiche N segments / K locuteurs détectés pour que
            l'utilisateur valide visuellement le parseur (formats de
            timestamp variés, ordre des champs variable) avant de lancer la
            fusion. Uniquement visible quand la fusion n'est pas encore
            faite (has_merged=false). */}
        {!allTranscripts?.summary?.has_merged && realSegs.length > 0 && (() => {
          const uniqueSpeakers = [...new Set(realSegs.map((s) => s.speaker).filter(Boolean))];
          return (
            <div
              className="mb-3 px-3 py-2 bg-amber-50 border border-amber-200 rounded-lg"
              data-testid="fireflies-preview-banner"
            >
              <p className="text-xs font-semibold text-amber-900">
                {lang === "fr"
                  ? `📋 Aperçu du parsing — validez avant de lancer la fusion`
                  : `📋 Parser preview — validate before running the merge`}
              </p>
              <p className="text-[11px] text-amber-800 mt-0.5">
                {lang === "fr" ? (
                  <>
                    <strong>{realSegs.length}</strong> segments détectés ·{" "}
                    <strong>{uniqueSpeakers.length}</strong> locuteur{uniqueSpeakers.length > 1 ? "s" : ""}
                    {uniqueSpeakers.length > 0 && (
                      <> — {uniqueSpeakers.slice(0, 3).join(", ")}
                        {uniqueSpeakers.length > 3 ? ` (+${uniqueSpeakers.length - 3})` : ""}</>
                    )}
                  </>
                ) : (
                  <>
                    <strong>{realSegs.length}</strong> segments detected ·{" "}
                    <strong>{uniqueSpeakers.length}</strong> speaker{uniqueSpeakers.length > 1 ? "s" : ""}
                    {uniqueSpeakers.length > 0 && (
                      <> — {uniqueSpeakers.slice(0, 3).join(", ")}
                        {uniqueSpeakers.length > 3 ? ` (+${uniqueSpeakers.length - 3})` : ""}</>
                    )}
                  </>
                )}
              </p>
              <p className="text-[10px] text-amber-700 mt-1">
                {lang === "fr"
                  ? "Si le parsing semble incorrect (mauvais locuteur, timestamps manquants), utilisez « Remplacer » pour ré-importer un fichier au format plus standard, puis lancez la fusion."
                  : "If parsing looks off (wrong speaker, missing timestamps), use 'Replace' to re-import with a more standard format, then run the merge."}
              </p>
            </div>
          );
        })()}
        {/* Eric (2026-05-12) — Affichage 3-cols (timecode | locuteur | texte) avec
            auto-scroll sur le segment actif (parité COMPLÈTE avec Whisper). */}
        {realSegs.length > 0 ? (
          <div className="px-3 py-3" data-testid="fireflies-segments">
            {realSegs.map((seg, fi) => {
              const speakerColor = speakerMap[seg.speaker] || "#71717a";
              const nextStart = realSegs[fi + 1]?.start ?? Number.POSITIVE_INFINITY;
              const isActive = currentTime >= seg.start && currentTime < nextStart;
              // Vincent #100 — timecode==0 sans index 0 = paragraphe sans timecode
              const hasTimecode = seg.start > 0 || fi === 0;
              return (
                <div
                  key={seg.id || fi}
                  ref={isActive ? activeSegmentRef : null}
                  className={`grid grid-cols-[70px_90px_1fr] gap-3 items-start py-1.5 cursor-pointer rounded transition-colors ${
                    isActive ? "bg-[#002FA7]/8" : "hover:bg-zinc-50"
                  }`}
                  onClick={() => hasTimecode && seekTo(seg.start)}
                  data-testid={`fireflies-segment-${fi}`}
                  {...(hasTimecode ? { "data-temps": seg.start ?? 0 } : {})}
                >
                  {hasTimecode ? (
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        seekTo(seg.start);
                      }}
                      className={`text-[11px] font-mono text-left tabular-nums leading-relaxed ${
                        isActive ? "text-[#002FA7] font-bold" : "text-zinc-400 hover:text-[#002FA7]"
                      }`}
                      data-testid={`fireflies-timecode-${fi}`}
                    >
                      {formatTimeBracket(seg.start)}
                    </button>
                  ) : (
                    <span
                      className="text-[11px] font-mono text-zinc-300 leading-relaxed"
                      data-testid={`fireflies-timecode-${fi}`}
                    >
                      —
                    </span>
                  )}
                  {/* B776 (Eric 2026-02-17 SPEC STRICK PROD) — Speaker sur 2 lignes */}
                  <div
                    className="text-[11px] font-semibold leading-tight break-words"
                    style={{ color: speakerColor }}
                    data-testid={`fireflies-speaker-${fi}`}
                    title={seg.speaker || "Inconnu"}
                  >
                    {formatSpeakerLines(seg.speaker).map((line, li) => (
                      <div key={li}>{line}</div>
                    ))}
                  </div>
                  <div
                    className={`text-sm leading-relaxed ${
                      isActive ? "text-zinc-900 font-medium" : "text-zinc-700"
                    }`}
                    data-testid={`fireflies-text-${fi}`}
                  >
                    {seg.text}
                  </div>
                  {/* Vincent B943 (Eric 2026-02-14) — Encadré correction factuelle. */}
                  <WebCorrectionBadge corrections={seg.web_corrections} lang={lang} />
                </div>
              );
            })}
          </div>
        ) : !rawText ? (
          <div className="p-10 text-center text-zinc-500" data-testid="fireflies-empty">
            <p className="text-sm font-medium text-zinc-700">
              {lang === "fr" ? "Aucune transcription Fireflies pour cette vidéo" : "No Fireflies transcript for this video"}
            </p>
            {/* Vincent B969 (Eric 2026-02-14 SPEC STRICK PROD) — Message
                actionnable : indique EXACTEMENT quel bouton utiliser pour
                importer une transcription Fireflies. */}
            <p className="mt-2 text-xs text-zinc-500 max-w-md mx-auto">
              {lang === "fr"
                ? "Utilise le bouton « 📥 Remplacer transcription Fireflies » dans la barre d'outils ci-dessus pour importer un fichier .txt / .vtt / .srt / .json exporté depuis Fireflies ou Assembly AI."
                : "Use the « 📥 Replace Fireflies transcript » button in the toolbar above to import a .txt / .vtt / .srt / .json file exported from Fireflies or Assembly AI."}
            </p>
          </div>
        ) : (
          // Vincent #100 — Fallback "aucun segment parsable" : phrases en pseudo-3-cols
          <div className="px-3 py-3" data-testid="fireflies-segments-flat">
            {sentences.map((sent, fi) => (
              <div
                key={fi}
                className="grid grid-cols-[70px_90px_1fr] gap-3 items-start py-1.5 rounded hover:bg-zinc-50"
                data-testid={`fireflies-segment-${fi}`}
              >
                <span
                  className="text-[11px] font-mono text-zinc-300 leading-relaxed"
                  data-testid={`fireflies-timecode-${fi}`}
                >
                  —
                </span>
                <div
                  className="text-[12px] font-semibold leading-relaxed truncate text-zinc-400"
                  data-testid={`fireflies-speaker-${fi}`}
                >
                  —
                </div>
                <div
                  className="text-sm leading-relaxed text-zinc-700"
                  data-testid={`fireflies-text-${fi}`}
                >
                  {sent}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
      </div>
      )}
    </>
  );
};

export default FirefliesSubTab;
